import type { ReactNode } from 'react';
import {
  TableHeaderLabel,
  type TableHeaderSort,
} from '../TableHeaderLabel/TableHeaderLabel';
import styles from './TableHeaderCell.module.css';

/**
 * Figma: `Table header cell` (323:6417) — one column heading. 36px tall,
 * `space/12` padding, `space/6` between the optional select-all checkbox and
 * the label. The 36 is FIXED in Figma, so a taller label — a help icon makes
 * one 16 — centres inside the cell rather than growing it. See the stylesheet:
 * CSS needs the content band declared to reproduce that.
 *
 * `Text` and `Checkbox` are Figma booleans for "is there a label" and "is there
 * a checkbox", so neither becomes a prop in its own right: passing `children`
 * is `Text=True`, passing `checkbox` is `Checkbox=True`, and passing neither
 * gives the `Text=False, Checkbox=False` spacer.
 *
 * `checkbox` is a slot rather than a set of checked/onChange props because the
 * select-all state belongs to whatever owns the rows — Figma models it as an
 * instance for the same reason.
 */
export type TableHeaderCellProps = {
  /** Figma `Text=True` — the column name. Omit for a spacer cell. */
  children?: string;
  /** Figma `Arrow` on the nested label. */
  sort?: TableHeaderSort;
  /** Figma `Help icon=True` on the nested label. */
  helpText?: string;
  helpSupportingText?: string;
  /** Figma `Checkbox=True` — the select-all control. */
  checkbox?: ReactNode;
  /**
   * Makes the label activate a sort. Not in Figma, which draws arrows but no
   * control — a header you cannot click cannot sort.
   */
  onSort?: () => void;
  className?: string;
};

/** `aria-sort` only means anything on a column that sorts. Not in Figma. */
const ariaSort = {
  none: undefined,
  selector: 'none',
  asc: 'ascending',
  desc: 'descending',
} as const;

export function TableHeaderCell({
  children,
  sort = 'none',
  helpText,
  helpSupportingText,
  checkbox,
  onSort,
  className,
}: TableHeaderCellProps) {
  const label =
    children === undefined ? null : (
      <TableHeaderLabel sort={sort} helpText={helpText} helpSupportingText={helpSupportingText}>
        {children}
      </TableHeaderLabel>
    );

  return (
    <th
      className={[styles.cell, className].filter(Boolean).join(' ')}
      scope="col"
      aria-sort={ariaSort[sort]}
    >
      <span className={styles.content}>
        {checkbox !== undefined && <span className={styles.checkbox}>{checkbox}</span>}
        {/*
         * A sortable heading is a real button so it can be reached and fired
         * from the keyboard. Without `onSort` the label stays inert text, which
         * is what Figma draws.
         */}
        {onSort !== undefined && label !== null ? (
          <button className={styles.sortButton} type="button" onClick={onSort}>
            {label}
          </button>
        ) : (
          label
        )}
      </span>
    </th>
  );
}
